/*
 * 0.89 (K1): „Worum es geht“ – Boardbeschreibung (maßgeblicher Text), erwartetes Ergebnis und bis zu fünf wichtige
 * Bezüge (Karte, Bibliotheksdatei, sicherer Link). Eine Fassung, ein Bearbeitungsort: dieses Panel. Die Boardübersicht
 * zeigt denselben Inhalt kompakt. Ohne Angaben erscheint nirgends ein leerer Kasten.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, CheckCircle2, Compass, ExternalLink, FileText, Library, Link2, Plus, SquareKanban, Trash2 } from 'lucide-react';
import {
  CONTEXT_MAX_REFS,
  CONTEXT_OUTCOME_MAX,
  CONTEXT_PURPOSE_MAX,
  hasContext,
  type BoardContext as BoardContextT,
  type BoardSnapshot,
  type ContextRef,
  type ContextRefInput,
  type LibraryFile,
} from '@kaba/shared';
import { api, ApiError } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { openCard } from '../../lib/cardNav';
import { contextKey, useBoardContext } from '../../lib/context';
import { libraryPreviewItem } from '../../lib/library';
import { toast } from '../../lib/toast';
import { AttachmentPreview } from '../card/AttachmentPreview';
import { LibraryPickDialog } from '../library/LibraryPickDialog';
import { Button, cx, IconButton, Sheet } from '../ui/ui';
import { inputCls } from '../AdminPage';

type Draft = { description: string; outcome: string; refs: (ContextRefInput & { key: string; label: string })[]; keepUnavailable: string[] };

const toDraft = (c: BoardContextT): Draft => ({
  description: c.description ?? '',
  outcome: c.outcome ?? '',
  refs: c.refs.flatMap((r): Draft['refs'] =>
    r.kind === 'card'
      ? [{ kind: 'card' as const, cardId: r.cardId, purpose: r.purpose, key: r.id, label: r.title }]
      : r.kind === 'file'
        ? [{ kind: 'file' as const, libraryFileId: r.libraryFileId, purpose: r.purpose, key: r.id, label: r.file.name }]
        : r.kind === 'link'
          ? [{ kind: 'link' as const, url: r.url, title: r.title, purpose: r.purpose, key: r.id, label: r.title }]
          : [],
  ),
  keepUnavailable: c.refs.filter((r) => r.kind === 'unavailable').map((r) => r.id),
});

const keyOf = () => Math.random().toString(36).slice(2);

/** Einstieg am Boardtitel – nur, wenn es Angaben gibt (sonst bleibt der Kopf schlicht) */
export function ContextButton({ boardId, onOpen }: { boardId: string; onOpen: () => void }) {
  const { data } = useBoardContext(boardId);
  if (!hasContext(data)) return null;
  return (
    <IconButton label="Worum es geht" onClick={onOpen} className="shrink-0">
      <Compass size={19} />
    </IconButton>
  );
}

/** Panel „Worum es geht“ (Lesen und – für Besitzer*innen – Bearbeiten) */
export function ContextSheet({ open, edit, onClose, snapshot }: { open: boolean; edit: boolean; onClose: () => void; snapshot: BoardSnapshot }) {
  const boardId = snapshot.board.id;
  const { data, error } = useBoardContext(boardId, open);
  const [editing, setEditing] = useState(edit);
  useEffect(() => {
    if (open) setEditing(edit);
  }, [open, edit]);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Worum es geht" side="right">
      <div className="flex flex-col gap-4 p-4">
        {error && !data && (
          <p role="alert" className="text-sm text-rose-700 dark:text-rose-300">
            {(error as Error).message}
          </p>
        )}
        {!data && !error && <p className="text-sm text-[var(--ink-soft)]">Lädt …</p>}
        {data &&
          (editing && data.canEdit ? (
            <ContextEditor ctx={data} snapshot={snapshot} onDone={() => setEditing(false)} />
          ) : (
            <>
              <ContextView ctx={data} snapshot={snapshot} onOpenCard={(id) => (onClose(), openCard(id))} />
              {!hasContext(data) && <p className="text-sm text-[var(--ink-soft)]">Für dieses Board ist noch nichts eingetragen.</p>}
              {data.canEdit && (
                <Button className="self-start" onClick={() => setEditing(true)}>
                  {hasContext(data) ? 'Bearbeiten' : 'Ziel und Bezüge eintragen'}
                </Button>
              )}
              <p className="text-xs text-[var(--ink-soft)]">Alle mit Zugriff auf das Board sehen diese Angaben – auch Gäste (ohne Bibliotheksdateien).</p>
            </>
          ))}
      </div>
    </Sheet>
  );
}

/** Kompakter Abschnitt oben in der Boardübersicht – nur mit Angaben */
export function ContextSection({ snapshot, onEdit }: { snapshot: BoardSnapshot; onEdit: () => void }) {
  const { data } = useBoardContext(snapshot.board.id);
  if (!data || !hasContext(data)) return null;
  return (
    <section aria-labelledby="ctx-title" className="mb-3 flex flex-col gap-3 rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-4">
      <header className="flex items-center gap-2">
        <Compass size={18} className="text-[var(--ink-soft)]" aria-hidden />
        <h2 id="ctx-title" className="flex-1 text-base font-semibold">
          Worum es geht
        </h2>
        {data.canEdit && (
          <Button size="sm" variant="ghost" onClick={onEdit}>
            Bearbeiten
          </Button>
        )}
      </header>
      <ContextView ctx={data} snapshot={snapshot} onOpenCard={(id) => openCard(id)} compact />
    </section>
  );
}

function ContextView({ ctx, snapshot, onOpenCard, compact }: { ctx: BoardContextT; snapshot: BoardSnapshot; onOpenCard: (id: string) => void; compact?: boolean }) {
  const [preview, setPreview] = useState<LibraryFile | null>(null);
  const live = useMemo(() => new Map(snapshot.cards.map((c) => [c.id, c])), [snapshot.cards]);
  const refs = ctx.refs.filter((r) => r.kind !== 'unavailable');
  const unavailable = ctx.refs.length - refs.length;
  return (
    <div className={cx('flex flex-col', compact ? 'gap-2.5' : 'gap-4')}>
      {ctx.description?.trim() && <p className="text-sm whitespace-pre-line">{ctx.description}</p>}
      {ctx.outcome && (
        <div>
          <h3 className="text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">Erwartetes Ergebnis</h3>
          <p className="text-sm">{ctx.outcome}</p>
        </div>
      )}
      {refs.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">Wichtige Bezüge</h3>
          <ul className="mt-1 flex flex-col">
            {refs.map((r) => (
              <li key={r.id}>
                <RefRow r={r} live={live} onOpenCard={onOpenCard} onPreview={setPreview} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {unavailable > 0 && ctx.canEdit && (
        <p className="text-xs text-amber-800 dark:text-amber-300">
          {unavailable === 1 ? 'Ein Bezug ist nicht mehr verfügbar' : `${unavailable} Bezüge sind nicht mehr verfügbar`} (gelöscht oder nicht mehr freigegeben) – unter „Bearbeiten“ entfernen.
        </p>
      )}
      {preview && <AttachmentPreview items={[libraryPreviewItem(preview)]} index={0} onIndex={() => undefined} onClose={() => setPreview(null)} />}
    </div>
  );
}

const rowCls = '-mx-2 flex min-h-12 w-full items-start gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-black/[0.03] dark:hover:bg-white/[0.04]';

function RefRow({
  r,
  live,
  onOpenCard,
  onPreview,
}: {
  r: Exclude<ContextRef, { kind: 'unavailable' }>;
  live: Map<string, BoardSnapshot['cards'][number]>;
  onOpenCard: (id: string) => void;
  onPreview: (f: LibraryFile) => void;
}) {
  const purpose = r.purpose ? <span className="block text-xs text-[var(--ink-soft)]">{r.purpose}</span> : null;
  if (r.kind === 'card') {
    // Titel und Stand live aus dem Board (Änderungen erscheinen ohne zweite Pflege)
    const c = live.get(r.cardId);
    const title = c?.title ?? r.title;
    const done = c ? !!c.completedAt : r.done;
    const archived = !c && r.archived;
    return (
      <button type="button" className={rowCls} onClick={() => onOpenCard(r.cardId)}>
        <SquareKanban size={16} className="mt-0.5 shrink-0 text-[var(--ink-soft)]" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className={cx('block text-sm font-medium break-words', archived && 'text-[var(--ink-soft)]')}>
            {title}
            {done && <CheckCircle2 size={14} className="ml-1 inline text-emerald-600" aria-label="erledigt" />}
            {archived && <span className="ml-1.5 rounded bg-black/5 px-1 text-xs font-normal dark:bg-white/10">archiviert</span>}
          </span>
          {purpose}
        </span>
      </button>
    );
  }
  if (r.kind === 'file')
    return (
      <button type="button" className={rowCls} onClick={() => onPreview(r.file)}>
        <FileText size={16} className="mt-0.5 shrink-0 text-[var(--ink-soft)]" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium break-words">{r.file.name}</span>
          <span className="block text-xs text-[var(--ink-soft)]">
            {r.team ? 'Team-Bibliothek' : 'Bibliothek'} · Fassung {r.file.version}
            {r.purpose ? ` · ${r.purpose}` : ''}
          </span>
        </span>
      </button>
    );
  return (
    <a href={r.url} target="_blank" rel="noopener noreferrer nofollow" className={rowCls}>
      <ExternalLink size={16} className="mt-0.5 shrink-0 text-[var(--ink-soft)]" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium break-words">{r.title}</span>
        <span className="block truncate text-xs text-[var(--ink-soft)]">
          {new URL(r.url).host}
          {r.purpose ? ` · ${r.purpose}` : ''}
          <span className="sr-only"> (öffnet in neuem Tab)</span>
        </span>
      </span>
    </a>
  );
}

function ContextEditor({ ctx, snapshot, onDone }: { ctx: BoardContextT; snapshot: BoardSnapshot; onDone: () => void }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const boardId = snapshot.board.id;
  const [d, setD] = useState<Draft>(() => toDraft(ctx));
  const [base, setBase] = useState(ctx.version);
  const [conflict, setConflict] = useState<BoardContextT | null>(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState<null | 'card' | 'link'>(null);
  const [lib, setLib] = useState(false);
  const total = d.refs.length + d.keepUnavailable.length;
  const full = total >= CONTEXT_MAX_REFS;
  const set = (p: Partial<Draft>) => setD((x) => ({ ...x, ...p }));
  const move = (i: number, j: number) =>
    setD((x) => {
      const refs = [...x.refs];
      const [r] = refs.splice(i, 1);
      refs.splice(j, 0, r!);
      return { ...x, refs };
    });
  const save = async () => {
    setSaving(true);
    try {
      const saved = await api.put<BoardContextT>(`/api/boards/${boardId}/context`, {
        description: d.description,
        outcome: d.outcome,
        refs: d.refs.map(({ key: _k, label: _l, ...r }) => r),
        keepUnavailable: d.keepUnavailable,
        expectedVersion: base,
      });
      qc.setQueryData(contextKey(boardId), saved);
      toast.info('„Worum es geht“ gespeichert.');
      onDone();
    } catch (e) {
      const cur = (e as ApiError).status === 409 ? ((e as ApiError).data as { details?: { current?: BoardContextT } })?.details?.current : null;
      if (cur) setConflict(cur);
      else toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      {conflict && (
        <div role="alert" className="flex flex-col gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/50 dark:text-amber-100">
          Jemand hat „Worum es geht“ inzwischen geändert. Deine Eingaben sind noch da.
          <span className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setD(toDraft(conflict));
                setBase(conflict.version);
                setConflict(null);
              }}
            >
              Aktuellen Stand laden
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setBase(conflict.version);
                setConflict(null);
              }}
            >
              Meine Fassung behalten
            </Button>
          </span>
        </div>
      )}
      <label className="flex flex-col gap-1 text-sm" htmlFor="ctx-desc">
        <span className="font-semibold">Beschreibung des Boards</span>
        <textarea id="ctx-desc" rows={5} maxLength={5000} className={cx(inputCls, 'min-h-28 py-2')} value={d.description} onChange={(e) => set({ description: e.target.value })} placeholder="Wofür arbeiten wir hier? Wer ist beteiligt, was gilt?" />
      </label>
      <label className="flex flex-col gap-1 text-sm" htmlFor="ctx-outcome">
        <span className="font-semibold">Erwartetes Ergebnis (optional)</span>
        <input id="ctx-outcome" className={inputCls} maxLength={CONTEXT_OUTCOME_MAX} value={d.outcome} onChange={(e) => set({ outcome: e.target.value })} placeholder="z. B. „Neue Website ist online“" />
      </label>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-semibold">
          Wichtige Bezüge ({total} von {CONTEXT_MAX_REFS})
        </legend>
        {d.refs.map((r, i) => (
          <div key={r.key} className="flex flex-col gap-1.5 rounded-lg border border-[var(--line)] p-2">
            <div className="flex items-center gap-1">
              {r.kind === 'card' ? <SquareKanban size={15} aria-hidden /> : r.kind === 'file' ? <Library size={15} aria-hidden /> : <Link2 size={15} aria-hidden />}
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{r.kind === 'card' ? (snapshot.cards.find((c) => c.id === r.cardId)?.title ?? r.label) : r.label}</span>
              <IconButton type="button" label={`${r.label} nach oben`} disabled={i === 0} onClick={() => move(i, i - 1)}>
                <ArrowUp size={16} />
              </IconButton>
              <IconButton type="button" label={`${r.label} nach unten`} disabled={i === d.refs.length - 1} onClick={() => move(i, i + 1)}>
                <ArrowDown size={16} />
              </IconButton>
              <IconButton type="button" label={`Bezug „${r.label}“ entfernen`} onClick={() => set({ refs: d.refs.filter((x) => x.key !== r.key) })}>
                <Trash2 size={16} />
              </IconButton>
            </div>
            <input
              aria-label={`Zweck von „${r.label}“`}
              className={inputCls}
              maxLength={CONTEXT_PURPOSE_MAX}
              placeholder="Zweck (optional), z. B. „maßgebliches Briefing“"
              value={r.purpose ?? ''}
              onChange={(e) => set({ refs: d.refs.map((x) => (x.key === r.key ? { ...x, purpose: e.target.value } : x)) })}
            />
          </div>
        ))}
        {d.keepUnavailable.map((id) => (
          <div key={id} className="flex items-center gap-2 rounded-lg border border-dashed border-[var(--line)] p-2 text-sm text-[var(--ink-soft)]">
            <span className="flex-1">Nicht mehr verfügbarer Bezug</span>
            <Button type="button" size="sm" onClick={() => set({ keepUnavailable: d.keepUnavailable.filter((x) => x !== id) })}>
              Entfernen
            </Button>
          </div>
        ))}
        {adding === 'card' && (
          <CardPick
            snapshot={snapshot}
            exclude={d.refs.flatMap((r) => (r.kind === 'card' ? [r.cardId] : []))}
            onPick={(c) => {
              set({ refs: [...d.refs, { kind: 'card', cardId: c.id, purpose: null, key: keyOf(), label: c.title }] });
              setAdding(null);
            }}
            onCancel={() => setAdding(null)}
          />
        )}
        {adding === 'link' && (
          <LinkAdd
            onAdd={(title, url) => {
              set({ refs: [...d.refs, { kind: 'link', url, title, purpose: null, key: keyOf(), label: title }] });
              setAdding(null);
            }}
            onCancel={() => setAdding(null)}
          />
        )}
        {!adding && !full && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={() => setAdding('card')}>
              <Plus size={15} aria-hidden /> Karte
            </Button>
            {!me?.isGuest && (
              <Button type="button" size="sm" onClick={() => setLib(true)}>
                <Plus size={15} aria-hidden /> Datei aus der Bibliothek
              </Button>
            )}
            <Button type="button" size="sm" onClick={() => setAdding('link')}>
              <Plus size={15} aria-hidden /> Link
            </Button>
          </div>
        )}
        <p className="text-xs text-[var(--ink-soft)]">Bezüge sind Verweise: Titel und Fassung kommen immer aus dem Original. Entfernen löscht nur den Verweis.</p>
      </fieldset>
      <p className="text-xs text-[var(--ink-soft)]">Sichtbar für alle mit Zugriff auf das Board, auch für Gäste (Bibliotheksdateien nicht).</p>
      <div className="flex justify-end gap-2">
        <Button type="button" onClick={onDone}>
          Abbrechen
        </Button>
        <Button type="submit" variant="primary" disabled={saving}>
          Speichern
        </Button>
      </div>
      <LibraryPickDialog
        open={lib}
        boardId={boardId}
        onClose={() => setLib(false)}
        onPickFile={(f) => {
          if (d.refs.some((r) => r.kind === 'file' && r.libraryFileId === f.id)) return;
          set({ refs: [...d.refs, { kind: 'file', libraryFileId: f.id, purpose: null, key: keyOf(), label: f.name }] });
        }}
      />
    </form>
  );
}

function CardPick({ snapshot, exclude, onPick, onCancel }: { snapshot: BoardSnapshot; exclude: string[]; onPick: (c: { id: string; title: string }) => void; onCancel: () => void }) {
  const [q, setQ] = useState('');
  const hits = snapshot.cards.filter((c) => !exclude.includes(c.id) && (!q.trim() || c.title.toLowerCase().includes(q.trim().toLowerCase()))).slice(0, 8);
  return (
    <div className="flex flex-col gap-1.5 rounded-lg bg-[var(--column)] p-2">
      <input autoFocus aria-label="Karte suchen" className={inputCls} placeholder="Karte suchen …" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Escape' && (e.stopPropagation(), onCancel())} />
      <ul className="flex flex-col">
        {hits.map((c) => (
          <li key={c.id}>
            <button type="button" className="min-h-11 w-full rounded-md px-2 text-left text-sm hover:bg-black/5 sm:min-h-9 dark:hover:bg-white/10" onClick={() => onPick(c)}>
              {c.title}
            </button>
          </li>
        ))}
        {!hits.length && <li className="px-2 py-1 text-sm text-[var(--ink-soft)]">Keine passende Karte.</li>}
      </ul>
      <Button type="button" size="sm" variant="ghost" className="self-start" onClick={onCancel}>
        Abbrechen
      </Button>
    </div>
  );
}

function LinkAdd({ onAdd, onCancel }: { onAdd: (title: string, url: string) => void; onCancel: () => void }) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const ok = /^https:\/\/[^\s/]+[^\s]*$/i.test(url.trim()) && !!title.trim();
  const bad = !!url.trim() && !/^https:\/\/[^\s/]+[^\s]*$/i.test(url.trim());
  return (
    <div className="flex flex-col gap-1.5 rounded-lg bg-[var(--column)] p-2">
      <input autoFocus aria-label="Titel des Links" className={inputCls} maxLength={80} placeholder="Titel" value={title} onChange={(e) => setTitle(e.target.value)} />
      <input aria-label="Adresse" aria-invalid={bad || undefined} className={inputCls} maxLength={500} placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} />
      {bad && (
        <p role="alert" className="text-xs text-rose-700 dark:text-rose-300">
          Nur sichere Adressen mit https://
        </p>
      )}
      <span className="flex gap-2">
        <Button type="button" size="sm" disabled={!ok} onClick={() => onAdd(title.trim(), url.trim())}>
          Hinzufügen
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Abbrechen
        </Button>
      </span>
    </div>
  );
}

export function ContextHint({ children }: { children: ReactNode }) {
  return <p className="text-xs text-[var(--ink-soft)]">{children}</p>;
}
